import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  AlertTriangle, RefreshCw, Eye, Filter, X, MailCheck, Clock,
  RotateCcw, Truck, XCircle,
} from "lucide-react";
import { PageHead, Loader } from "../components/PageHead";
import { DataTable } from "../components/DataTable";
import { Badge, StatusBadge } from "../components/Badges";
import { EquipmentModal } from "../components/EquipmentModal";
import { UnserviceableReviewModal } from "../components/UnserviceableReviewModal";
import { fmtDate, fmtDateTime } from "../lib/format";
import { useAuth } from "../context/AuthContext";
import api, { apiErr } from "../lib/api";
import { toast } from "sonner";

const TABS = [
  ["awaiting", "Awaiting Review"],
  ["returned_customer", "Returned to Customer"],
  ["returned_tech", "Returned to Tech"],
  ["closed", "Closed"],
  ["scrapped", "Scrapped (Equipment)"],
];

const REASONS = [
  "damaged", "rusted", "burned_electronic", "over_pressurized",
  "fitting_damaged", "other",
];

const STATS = [
  { key: "awaiting_admin_review", label: "Awaiting admin review", icon: AlertTriangle, tone: "amber" },
  { key: "customer_advised_pending", label: "Customer advised, awaiting response", icon: MailCheck, tone: "blue" },
  { key: "returned_to_technician", label: "Returned to technician", icon: RotateCcw, tone: "violet" },
  { key: "ready_for_customer_return", label: "Ready to return to customer", icon: Truck, tone: "green" },
  { key: "closed_this_month", label: "Closed this month", icon: XCircle, tone: "slate" },
];

function StatTiles({ summary }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 10, marginBottom: 16 }}
      data-testid="usr-stats">
      {STATS.map((s) => {
        const Icon = s.icon;
        const v = summary[s.key] ?? 0;
        return (
          <div key={s.key} className="card pad" data-testid={`usr-stat-${s.key}`}
            style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <div style={{
              width: 36, height: 36, borderRadius: 9, flex: "none",
              display: "inline-flex", alignItems: "center", justifyContent: "center",
              background: "var(--card-2)", color: "var(--gold-deep)",
            }}>
              <Icon size={17} />
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 22, fontWeight: 700, lineHeight: 1 }}>{v}</div>
              <div className="t-mut" style={{ fontSize: 11.5, marginTop: 4 }}>{s.label}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function FilterBar({ filters, setFilters, customers, technicians, onClear }) {
  const set = (k, v) => setFilters((f) => ({ ...f, [k]: v }));
  const hasFilters = Object.values(filters).some((v) => v !== "" && v !== null && v !== undefined && v !== false);
  return (
    <div className="jfb-filters" data-testid="usr-filters" style={{ marginBottom: 14 }}>
      <div className="search" style={{ flex: "0 1 280px" }}>
        <input placeholder="Search DN, serial, asset, description…"
          value={filters.search} onChange={(e) => set("search", e.target.value)}
          data-testid="usr-filter-search" />
      </div>
      <select className="jfb-select" value={filters.customer_id}
        onChange={(e) => set("customer_id", e.target.value)} data-testid="usr-filter-customer">
        <option value="">All customers</option>
        {customers.map((c) => <option key={c.id} value={c.id}>{c.customer_name}</option>)}
      </select>
      <select className="jfb-select" value={filters.technician_id}
        onChange={(e) => set("technician_id", e.target.value)} data-testid="usr-filter-tech">
        <option value="">All technicians</option>
        {technicians.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
      </select>
      <select className="jfb-select" value={filters.reason}
        onChange={(e) => set("reason", e.target.value)} data-testid="usr-filter-reason">
        <option value="">All reasons</option>
        {REASONS.map((r) => <option key={r} value={r}>{r.replace(/_/g, " ")}</option>)}
      </select>
      <select className="jfb-select" value={filters.min_days}
        onChange={(e) => set("min_days", e.target.value)} data-testid="usr-filter-days">
        <option value="">Any days waiting</option>
        <option value="1">≥ 1 day</option>
        <option value="3">≥ 3 days</option>
        <option value="7">≥ 7 days</option>
        <option value="14">≥ 14 days</option>
      </select>
      <select className="jfb-select" value={filters.advised}
        onChange={(e) => set("advised", e.target.value)} data-testid="usr-filter-advised">
        <option value="">Advised: any</option>
        <option value="true">Advised: yes</option>
        <option value="false">Advised: no</option>
      </select>
      <select className="jfb-select" value={filters.review_outcome}
        onChange={(e) => set("review_outcome", e.target.value)} data-testid="usr-filter-outcome">
        <option value="">Follow-up: any</option>
        <option value="return_to_customer">Return to customer</option>
        <option value="return_to_technician">Return to technician</option>
        <option value="close">Closed</option>
      </select>
      {hasFilters && (
        <span className="jfb-chip" onClick={onClear} data-testid="usr-filter-clear">
          <X size={11} /> Clear
        </span>
      )}
    </div>
  );
}

function UnserviceableTab({ tab, refresh, onOpenReview, customers, technicians }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    search: "", customer_id: "", technician_id: "", reason: "",
    min_days: "", advised: "", review_outcome: "",
  });

  const params = useMemo(() => {
    const p = { tab };
    if (filters.search) p.search = filters.search;
    if (filters.customer_id) p.customer_id = filters.customer_id;
    if (filters.technician_id) p.technician_id = filters.technician_id;
    if (filters.reason) p.reason = filters.reason;
    if (filters.min_days) p.min_days_waiting = filters.min_days;
    if (filters.advised) p.customer_advised = filters.advised;
    if (filters.review_outcome) p.review_outcome = filters.review_outcome;
    return p;
  }, [tab, filters]);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/unserviceable", { params })
      .then((r) => setRows(r.data || []))
      .catch((e) => toast.error(apiErr(e)))
      .finally(() => setLoading(false));
  }, [params]);
  useEffect(() => { load(); }, [load, refresh]);

  const clearFilters = () => setFilters({
    search: "", customer_id: "", technician_id: "", reason: "",
    min_days: "", advised: "", review_outcome: "",
  });

  return (
    <>
      <FilterBar filters={filters} setFilters={setFilters}
        customers={customers} technicians={technicians} onClear={clearFilters} />
      {loading ? <Loader /> : !rows.length ? (
        <div className="t-mut" style={{ padding: 40, textAlign: "center" }}>
          No items match the current filters.
        </div>
      ) : (
        <div className="tbl-wrap">
          <table className="dt" data-testid={`usr-table-${tab}`}>
            <thead>
              <tr>
                <th>Equipment</th>
                <th>Customer</th>
                <th>Reason</th>
                <th>Raised by</th>
                <th>Days waiting</th>
                <th>Customer advised</th>
                <th>Status</th>
                <th>Outcome</th>
                <th style={{ width: 80 }}></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} data-testid={`usr-row-${r.id}`}
                  style={{ cursor: "pointer" }} onClick={() => onOpenReview(r.id)}>
                  <td>
                    <div className="t-strong">{r.description || "—"}</div>
                    <div className="mono t-mut" style={{ fontSize: 11.5 }}>
                      {r.serial_number ? "SN " + r.serial_number : r.issue_number ? "IN " + r.issue_number : "—"}
                      {r.delivery_note_number && <> · DN {r.delivery_note_number}</>}
                    </div>
                  </td>
                  <td className="t-mut">{r.customer_name || "—"}</td>
                  <td>
                    <Badge label={r.reason_label || r.reason || "—"} cls="b-amber" />
                  </td>
                  <td className="t-mut">{r.raised_by_name || "—"}</td>
                  <td className="mono">
                    {r.days_in_review != null ? (
                      <span className="t-mut" style={{ display: "inline-flex", alignItems: "center", gap: 3 }}>
                        <Clock size={11} /> {r.days_in_review}d
                      </span>
                    ) : "—"}
                  </td>
                  <td>
                    {r.customer_advised
                      ? <Badge label={`✓ Yes · ${r.customer_advised_count}`} cls="b-green" testid={`usr-advised-yes-${r.id}`} />
                      : <Badge label="⚠ No" cls="b-red" testid={`usr-advised-no-${r.id}`} />}
                  </td>
                  <td><StatusBadge status={r.status} /></td>
                  <td>
                    {r.review_outcome
                      ? <Badge label={r.review_outcome.replace(/_/g, " ")} cls="b-slate" />
                      : <span className="t-mut">—</span>}
                  </td>
                  <td>
                    <button className="btn ghost sm"
                      onClick={(e) => { e.stopPropagation(); onOpenReview(r.id); }}
                      data-testid={`usr-open-${r.id}`}>
                      <Eye size={12} /> Open
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

function ScrappedTab({ refresh, onOpenEquipment }) {
  const columns = [
    { key: "description", label: "Equipment", sortable: true, colSearch: true,
      render: (v, row) => <span className="cell-2" style={{ display: "inline-flex" }}>
        <span className="t-strong">{v || "—"}</span>
        <span className="s">{row.manufacturer || ""}</span>
      </span> },
    { key: "serial_number", label: "Serial", sortable: true, colSearch: true, mono: true,
      render: (v) => v ? v : <span style={{ color: "var(--red)", fontWeight: 600 }}>⚠ none</span> },
    { key: "issue_number", label: "Issue", sortable: true, colSearch: true, mono: true },
    { key: "customer_name", label: "Customer", sortable: true, colSearch: true,
      render: (v) => <span className="t-mut">{v || "—"}</span> },
    { key: "scrapped_reason", label: "Reason", sortable: true, colSearch: true,
      render: (v) => <span className="t-mut">{v || "—"}</span> },
    { key: "scrapped_by_name", label: "By",
      render: (v) => <span className="t-mut">{v || "—"}</span> },
    { key: "scrapped_at", label: "Date", sortable: true, mono: true,
      render: (v) => <span className="t-mut">{fmtDate(v)}</span> },
  ];
  return (
    <DataTable endpoint="/scrapped" columns={columns} rowKey="id" testid="scrapped" clientSide
      defaultSort={{ by: "scrapped_at", dir: "desc" }}
      searchPlaceholder="Search equipment, customer, reason…"
      emptyText="No scrapped equipment."
      onRowClick={(row) => onOpenEquipment(row.id)} refreshSignal={refresh} />
  );
}

export default function Scrapped() {
  const { can } = useAuth();
  const canEdit = can("equipment", "edit");
  const [tab, setTab] = useState("awaiting");
  const [refresh, setRefresh] = useState(0);
  const [reviewId, setReviewId] = useState(null);
  const [equipId, setEquipId] = useState(null);
  const [summary, setSummary] = useState({});
  const [customers, setCustomers] = useState([]);
  const [technicians, setTechnicians] = useState([]);

  const reload = () => setRefresh((x) => x + 1);

  useEffect(() => {
    api.get("/unserviceable/summary").then((r) => setSummary(r.data || {})).catch(() => {});
  }, [refresh]);

  useEffect(() => {
    api.get("/customers", { params: { limit: 500 } }).then((r) => {
      const items = r.data?.items || r.data || [];
      setCustomers(items.map((c) => ({ id: c.id, customer_name: c.customer_name }))
        .sort((a, b) => (a.customer_name || "").localeCompare(b.customer_name || "")));
    }).catch(() => {});
    api.get("/users/staff", { params: { limit: 200 } }).then((r) => {
      const items = r.data?.items || [];
      setTechnicians(items.filter((u) => u.role === "technician" && u.active !== false)
        .map((t) => ({ id: t.id, name: t.name }))
        .sort((a, b) => (a.name || "").localeCompare(b.name || "")));
    }).catch(() => {});
  }, []);

  const tabCounts = {
    awaiting: summary.awaiting_admin_review,
    returned_customer: summary.returned_customer,
    returned_tech: summary.returned_technician,
    closed: summary.closed,
  };

  return (
    <>
      <PageHead crumb="Operations" title="Unserviceable / Defective"
        sub="Items flagged by technicians as not fit for calibration. Review and choose to return to customer, return to technician with instructions, or close."
        actions={<button className="btn ghost sm" onClick={reload} data-testid="usr-refresh">
          <RefreshCw size={13} /> Refresh
        </button>} />

      <StatTiles summary={summary} />

      <div className="ftabs" style={{ marginBottom: 12 }}>
        {TABS.map(([k, l]) => (
          <button key={k} className={`ftab ${tab === k ? "active" : ""}`}
            onClick={() => setTab(k)} data-testid={`usr-tab-${k}`}>
            {l}
            {tabCounts[k] != null && <span className="c">{tabCounts[k]}</span>}
          </button>
        ))}
      </div>

      {tab === "awaiting" && (
        <div className="notice" style={{ marginBottom: 12,
          background: "#fff8e6", border: "1px solid #e3d28a" }}>
          <AlertTriangle size={16} />
          <span style={{ fontSize: 12.5 }}>
            <b>Awaiting your review:</b> a technician has marked these items as
            not fit for calibration. Click any row to decide whether to return
            to customer, return back to the technician, or close.
          </span>
        </div>
      )}

      {tab !== "scrapped" && (
        <UnserviceableTab tab={tab} refresh={refresh}
          customers={customers} technicians={technicians}
          onOpenReview={(id) => setReviewId(id)} />
      )}
      {tab === "scrapped" && (
        <ScrappedTab refresh={refresh}
          onOpenEquipment={(id) => setEquipId(id)} />
      )}

      {reviewId && (
        <UnserviceableReviewModal itemId={reviewId}
          onClose={() => setReviewId(null)}
          onChanged={reload} />
      )}
      {equipId && (
        <EquipmentModal id={equipId} canEdit={canEdit}
          onClose={() => setEquipId(null)}
          onChanged={reload} />
      )}
    </>
  );
}
